@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import dev.mobilearchitecture.web.components.layouts.MarkdownLayout as _dev_mobilearchitecture_web_components_layouts_MarkdownLayout
import dev.mobilearchitecture.web.components.layouts.PageLayout as _dev_mobilearchitecture_web_components_layouts_PageLayout
import dev.mobilearchitecture.web.pages.AboutPage as _dev_mobilearchitecture_web_pages_AboutPage
import dev.mobilearchitecture.web.pages.HomePage as _dev_mobilearchitecture_web_pages_HomePage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Mobile System Design Mock Interviews — mobilearchitecture.dev"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "dev.mobilearchitecture.web.components.layouts.MarkdownLayout",
            parentLayoutId = "dev.mobilearchitecture.web.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                dev.mobilearchitecture.web.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _dev_mobilearchitecture_web_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "dev.mobilearchitecture.web.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _dev_mobilearchitecture_web_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "dev.mobilearchitecture.web.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                dev.mobilearchitecture.web.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _dev_mobilearchitecture_web_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "dev.mobilearchitecture.web.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                dev.mobilearchitecture.web.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _dev_mobilearchitecture_web_pages_AboutPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", dev.mobilearchitecture.web.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", dev.mobilearchitecture.web.SubheadlineTextStyle)
        ctx.theme.registerStyle("footer", dev.mobilearchitecture.web.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", dev.mobilearchitecture.web.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("logo", dev.mobilearchitecture.web.components.sections.LogoStyle)
        ctx.theme.registerStyle("sessions-grid", dev.mobilearchitecture.web.components.sections.SessionsGridStyle)
        ctx.theme.registerStyle("session-card", dev.mobilearchitecture.web.components.sections.SessionCardStyle)
        ctx.theme.registerStyle("most-recommended-card", dev.mobilearchitecture.web.components.sections.MostRecommendedCardStyle)
        ctx.theme.registerStyle("most-recommended-badge", dev.mobilearchitecture.web.components.sections.MostRecommendedBadgeStyle)
        ctx.theme.registerStyle("markdown", dev.mobilearchitecture.web.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", dev.mobilearchitecture.web.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("hero-container", dev.mobilearchitecture.web.pages.HeroContainerStyle)
        ctx.theme.registerStyle("section-container", dev.mobilearchitecture.web.pages.SectionContainerStyle)
        ctx.theme.registerStyle("card", dev.mobilearchitecture.web.pages.CardStyle)
        ctx.theme.registerStyle("faq-item", dev.mobilearchitecture.web.pages.FaqItemStyle)
        ctx.theme.registerVariant("-circle", dev.mobilearchitecture.web.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", dev.mobilearchitecture.web.UncoloredButtonVariant
        )
        ctx.theme.registerVariant("-primary", dev.mobilearchitecture.web.PrimaryButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", dev.mobilearchitecture.web.components.sections.SideMenuSlideInAnim)
        ctx.theme.registerKeyframes("fade-in-up", dev.mobilearchitecture.web.pages.FadeInUp)
        dev.mobilearchitecture.web.initSiteStyles(ctx)
        dev.mobilearchitecture.web.initColorMode(ctx)
        dev.mobilearchitecture.web.initStyles(ctx)
        dev.mobilearchitecture.web.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        dev.mobilearchitecture.web.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
